import { useEffect, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight, Send, Mail, CheckCircle2, Loader2, XCircle, Clock, Pause } from "lucide-react";
import api from "@/lib/api";
import { Card, Badge } from "@/components/kit";

const STATUS_STYLES = {
  done: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" },
  running: { bg: "bg-sky-100", text: "text-sky-700", dot: "bg-sky-500" },
  queued: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  scheduled: { bg: "bg-violet-100", text: "text-violet-700", dot: "bg-violet-500" },
  paused: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  failed: { bg: "bg-rose-100", text: "text-rose-700", dot: "bg-rose-500" },
  cancelled: { bg: "bg-slate-100", text: "text-slate-500", dot: "bg-slate-400" },
};

const WEEKDAYS = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function BlastCalendar() {
  const [events, setEvents] = useState([]);
  const [now, setNow] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const year = now.getFullYear();
  const month = now.getMonth();
  const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  useEffect(() => {
    api.get(`/blast/calendar?month=${monthStr}`).then((r) => setEvents(r.data.events || [])).catch(() => setEvents([]));
  }, [monthStr]);

  const prev = () => { setNow(new Date(year, month - 1, 1)); setSelectedDay(null); };
  const next = () => { setNow(new Date(year, month + 1, 1)); setSelectedDay(null); };

  // Group events by day
  const eventsByDay = {};
  events.forEach((e) => {
    const day = parseInt((e.date || "").split("-")[2], 10);
    if (day) {
      if (!eventsByDay[day]) eventsByDay[day] = [];
      eventsByDay[day].push(e);
    }
  });

  const monthName = now.toLocaleDateString("id-ID", { month: "long", year: "numeric" });

  // Calendar grid cells
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedEvents = selectedDay ? (eventsByDay[selectedDay] || []) : [];

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-lg flex items-center gap-2">
          <Calendar className="w-5 h-5 text-sky-500" /> Kalender Blast
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={prev} className="p-1.5 rounded-lg hover:bg-slate-100 transition"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-semibold text-slate-700 min-w-[140px] text-center">{monthName}</span>
          <button onClick={next} className="p-1.5 rounded-lg hover:bg-slate-100 transition"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-px mb-1">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-center text-[11px] font-semibold text-slate-400 py-1">{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-xl overflow-hidden">
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} className="bg-white min-h-[72px]" />;
          const dayEvents = eventsByDay[day] || [];
          const isToday = new Date().getFullYear() === year && new Date().getMonth() === month && new Date().getDate() === day;
          const isSelected = selectedDay === day;
          return (
            <div
              key={day}
              onClick={() => setSelectedDay(isSelected ? null : day)}
              className={`bg-white min-h-[72px] p-1.5 cursor-pointer transition hover:bg-slate-50 ${isSelected ? "ring-2 ring-sky-400 ring-inset" : ""}`}
            >
              <span className={`text-xs font-semibold ${isToday ? "bg-sky-500 text-white rounded-full w-5 h-5 inline-flex items-center justify-center" : "text-slate-700"}`}>
                {day}
              </span>
              <div className="mt-0.5 space-y-0.5">
                {dayEvents.slice(0, 3).map((e) => {
                  const st = STATUS_STYLES[e.status] || STATUS_STYLES.queued;
                  return (
                    <div key={e.id} className={`text-[10px] leading-tight px-1 py-0.5 rounded ${st.bg} ${st.text} truncate flex items-center gap-0.5`}>
                      {e.blast_type === "email" ? <Mail className="w-2.5 h-2.5 shrink-0" /> : <Send className="w-2.5 h-2.5 shrink-0" />}
                      <span className="truncate">{e.sent}/{e.total}</span>
                    </div>
                  );
                })}
                {dayEvents.length > 3 && <span className="text-[9px] text-slate-400">+{dayEvents.length - 3}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected day detail */}
      {selectedDay && selectedEvents.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold text-slate-500">{selectedDay} {monthName}</p>
          {selectedEvents.map((e) => {
            const st = STATUS_STYLES[e.status] || STATUS_STYLES.queued;
            return (
              <div key={e.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition">
                <div className={`w-2 h-2 rounded-full ${st.dot}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{e.title}</p>
                  <p className="text-xs text-slate-400">{e.sent}/{e.total} terkirim · {e.failed} gagal</p>
                </div>
                <Badge color={e.status === "done" ? "green" : e.status === "running" ? "sky" : e.status === "failed" ? "red" : "amber"}>
                  {e.status}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
